import {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import {SizeThematic} from "@esWebAPI/es/model/Thematic";
import {DataDrivenPropertyValueSpecification} from "@maplibre/maplibre-gl-style-spec";
import {GeomUtils} from "@esWebAPI/es/util/GeomUtils";
import {LAYER_DATA_LIMITS} from "@esWebAPI/es/map/layer/azure/AzureLayer";

export class SizeThematicStyler {
    private _selection: boolean = false;

    /**
     * Build an expression returning the attribute value.
     */
    private getAttrValue(layerView: ILayerView): DataDrivenPropertyValueSpecification<any> {
        const sizeAttr = layerView.styleSettings.sizeThematicSettings?.attributeName;
        return ['get', sizeAttr];
    }

    public getThematic(layerView: ILayerView): Promise<SizeThematic|undefined> {
        const overrideThematic = layerView.styleSettings.sizeThematicSettings?.overrideThematic as SizeThematic;

        let promise: Promise<SizeThematic | undefined>;

        if (overrideThematic) {
            promise = Promise.resolve(overrideThematic);
        } else if (layerView.styleSettings.sizeThematicSettings?.enabled) {
            promise = es.api.thematic.getSizeThematic(layerView.id, layerView.styleSettings.sizeThematicSettings.attributeName, {noCache: true});
        } else {
            promise = Promise.resolve(undefined);
        }

        return promise;
    }

    /**
     * Get the final computed icon size
     * @param layerView
     * @param sizeThematic
     */
    public getExpression(layerView: ILayerView, sizeThematic?: SizeThematic): DataDrivenPropertyValueSpecification<number> {

        if (!sizeThematic || sizeThematic.minValue === sizeThematic.maxValue) {
            return this._selection ? layerView.styleSettings.defaultStyle.size * 2 : layerView.styleSettings.defaultStyle.size;
        }

        return [
            'interpolate',
            ['linear'],
            this.getAttrValue(layerView),
            sizeThematic.minValue, this._selection ? sizeThematic.minSize * 2 : sizeThematic.minSize,
            sizeThematic.maxValue, this._selection ? sizeThematic.maxSize * 2 : sizeThematic.maxSize
        ];
    }

    /**
     * Build the part of expression required for the icon imageId. Get the original image size (eg. es_0_48_000000, where 48 is the size).
     * The icon is then scaled with the expression generated by getSizeScaleExpression applied to the iconOptions.size property.
     */
    public getIconSizeExpression(layerView: ILayerView, sizeThematic?: SizeThematic): DataDrivenPropertyValueSpecification<number> {

        if (sizeThematic && layerView.styleSettings.sizeThematicSettings?.enabled) {
            return sizeThematic.maxSize;
        }

        const symbolStyle = layerView.styleSettings.defaultStyle.$type === "SymbolAndPolygonStyle" ?
            layerView.styleSettings.defaultStyle.symbolStyle : layerView.styleSettings.defaultStyle;
        return symbolStyle.size;
    }

    /**
     * Build the expression required to scale the symbol icon. Get the scaled image size.
     */
    public getSizeScaleExpression(layerView: ILayerView, sizeThematic?: SizeThematic): DataDrivenPropertyValueSpecification<number> {
        let rs;
        if (!sizeThematic || !layerView.styleSettings.sizeThematicSettings?.enabled) {
            rs = 1;
        } else if(sizeThematic.minValue === sizeThematic.maxValue){
            rs = 1;
        } else {
            rs = [
                'interpolate',
                ['linear'],
                this.getAttrValue(layerView),
                sizeThematic.minValue, ['/', this.getExpression(layerView, sizeThematic), sizeThematic.maxSize],
                sizeThematic.maxValue, 1
            ];
        }
        if (rs !== 1){
            es.log.log("getSizeExpression", rs);
        }
        return rs as DataDrivenPropertyValueSpecification<number>;
    }

    public getRadius(layerView: ILayerView, layerInfo: LayerInfo, sizeThematic?: SizeThematic): DataDrivenPropertyValueSpecification<number>{

        if (layerView.styleSettings?.sizeThematicSettings?.enabled){
            //return this.getExpression(layerView, sizeThematic)
            return ['/', this.getExpression(layerView, sizeThematic), 2];
        }

        let defaultStyle = layerView.styleSettings.defaultStyle.$type === "SymbolAndPolygonStyle" ?
            layerView.styleSettings.defaultStyle.symbolStyle :
            layerView.styleSettings.defaultStyle;

        if (!defaultStyle.zoomScaleDependentSizing){
            return this._selection ? defaultStyle.size : defaultStyle.size / 2;
        }

        const size = defaultStyle.size as number;

        let radius: any[];

        const pointCount = layerInfo.featureInfo.count;
        const mbrArea = GeomUtils.calculateMbrArea(layerInfo.featureInfo.bounds);

        //  bias the density score higher to account for the fact that most datasets are irregular shaped - until we get a better density score
        const adjustmentFactor = 3;
        let density = (pointCount / mbrArea) * adjustmentFactor;
        // compensate for "huge" datasets until we have a better density score (ideally in fsInfo)
        if (pointCount >= LAYER_DATA_LIMITS.MAX_FEATS_FOR_ENTIRE_GEOJSON_LOAD) {
            density = 51;
        } else if(pointCount >= LAYER_DATA_LIMITS.MAX_FEATS_FOR_ENTIRE_GEOJSON_LOAD / 2){
            density = 50;
        }

        const radiusBase = 1.75;

        // If a point is explicitly set to a size that's smaller than our calc min size then use that (user knows best)
        const getSize = (minSize: number, adjustmentDivider: number) => {
            const calcSize = Math.max(minSize, Math.round(size / adjustmentDivider));
            if(size < calcSize){
                return size;
            }
            return calcSize;
        };

        // Adjust radius expression based on density
        if (density <= 1) {
            radius = [
                "interpolate", ["exponential", radiusBase],
                ["zoom"],
                4, getSize(5, 3),  // 8
                22, Math.round(size * 5) + 150         // 250
            ];
        } else if (density <= 10) {
            radius = [
                "interpolate", ["exponential", radiusBase],
                ["zoom"],
                12, getSize(5, 4), // 6
                22, Math.round(size * 5) + 100         // 200
            ];
        } else if (density <= 50) {
            radius = [
                "interpolate", ["exponential", radiusBase],
                ["zoom"],
                12, getSize(4, 5), // 4
                22, Math.round(size * 3) + 100         // 175
            ];
        } else {
            radius = [
                "interpolate", ["exponential", radiusBase],
                ["zoom"],
                12, getSize(3, 5), // 2
                22, Math.round(size * 2) + 50          // 100
            ];
        }
        if (this.selection) {
            radius[4] = radius[4] * 2;
            radius[6] = radius[6] * 2;
        }

        //es.log.log("getRadius", {pointCount, mbrArea, density, radius});

        return radius as DataDrivenPropertyValueSpecification<number>;
    }

    public get selection(): boolean {
        return this._selection;
    }

    public set selection(value: boolean) {
        this._selection = value;
    }
}
